import { Html } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { MantineOverrideProvider } from '@uag/react-core';
import { useEffect, useRef, useState } from 'react';
import { DoubleSide, type Group } from 'three';
import { useShallow } from 'zustand/shallow';

import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';
import { useTransformationMeshData } from 'app/modules/wallforming/components/scenes/objects/articles/hooks/useTransformationMeshData';
import { convertToDegree } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { isSingleFillerInstanceSelection } from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { PivotControls } from 'app/modules/wallforming/components/scenes/tools/pivot-controls';
import { TransformationLabel } from 'app/modules/wallforming/components/scenes/tools/pivot-controls/TransformationLabel';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { PRIMARY_COLOR_ACCENT_HEX } from 'shared/constants';

import type { OnDragStartProps, SelectedControl } from 'app/modules/wallforming/components/scenes/tools/pivot-controls/context';

export const PivotControlsHandler = () => {
    const {
        pivotControlsRef,
        setPivotControlsRef,
        handleOnArticleTransformation,
        handleOnArticleTransformationStart,
        handleOnArticleTransformationEnd,
        setIsArticleDirectlySelectedRef,
        setTransformationLabel
    } = usePlacementStore(
        useShallow((state) => ({
            pivotControlsRef: state.pivotControlsRef,
            setPivotControlsRef: state.setPivotControlsRef,
            handleOnArticleTransformation: state.handleOnArticleTransformation,
            handleOnArticleTransformationStart: state.handleOnArticleTransformationStart,
            handleOnArticleTransformationEnd: state.handleOnArticleTransformationEnd,
            setIsArticleDirectlySelectedRef: state.setIsArticleDirectlySelectedRef,
            setTransformationLabel: state.setTransformationLabel
        }))
    );

    const boxData = useTransformationMeshData();

    const prevRadians = useRef<number>(boxData ? boxData.anchorRotation : 0);
    const snappingRotation = useRef<number>(0);
    const positionUpdated = useRef<boolean>(false);

    const gl = useThree((state) => state.gl);

    const [selectedControl, setSelectedControl] = useState<SelectedControl | null>(null);
    const selectedControlRef = useRef<SelectedControl | null>(null);
    const selectionKey = usePlacementStore((state) =>
        Object.keys(state.articleInstanceSelections).sort().join('|')
    );
    const previousSelectionKey = useRef(selectionKey);
    const canScaleSelection = usePlacementStore((state) =>
        isSingleFillerInstanceSelection(Object.keys(state.articleInstanceSelections), state)
    );

    useEffect(() => {
        if (previousSelectionKey.current === selectionKey) {
            return;
        }

        previousSelectionKey.current = selectionKey;
        selectedControlRef.current = null;
        setSelectedControl(null);
    }, [selectionKey]);

    useEffect(() => {
        if (boxData && !selectedControl) {
            setTransformationLabel(convertToDegree(boxData.anchorRotation));
        }
    }, [boxData, selectedControl, setTransformationLabel]);

    const handlePivotControlsRef = (pivotControls: Group | null) => {
        if (pivotControls) {
            pivotControls.name = 'raycastable';
            setPivotControlsRef(pivotControls);

            if (pivotControlsRef.current) {
                pivotControlsRef.current.traverse((object) => {
                    object.layers.enable(LayerType.RAYCAST);
                });
            }
        }
        prevRadians.current = boxData ? boxData.anchorRotation : 0;
    };

    const handleOnDragArticleStart = () => {
        setIsArticleDirectlySelectedRef(true);
    };

    const handleOnPointerOver = () => {
        gl.domElement.style.setProperty('cursor', 'pointer');
    };

    const handleOnPointerOut = () => {
        gl.domElement.style.setProperty('cursor', 'auto');
    };

    const handleOnDragStart = (properties: OnDragStartProps) => {
        const control = { component: properties.component, axis: properties.axis };
        selectedControlRef.current = control;
        setSelectedControl(control);
        if (properties.component === 'Arrow') {
            setTransformationLabel(0);
        } else if (properties.component !== 'Sphere') {
            setTransformationLabel(convertToDegree(prevRadians.current));
        }
        handleOnArticleTransformationStart(prevRadians, positionUpdated, {
            component: properties.component,
            axis: properties.axis
        });
    };

    if (!boxData) {
        return null;
    }

    return (
        <group
            userData={{ isPivotControl: true }}
            onPointerOut={handleOnPointerOut}
            onPointerOver={handleOnPointerOver}
        >
            <PivotControls
                ref={handlePivotControlsRef}
                activeAxes={[true, true, false]}
                anchor={[0, 0, 0]}
                autoTransform={false}
                axisColors={['#ff0000', '#00ff00', PRIMARY_COLOR_ACCENT_HEX]}
                depthTest={false}
                disableScaling={!canScaleSelection}
                hoveredColor={undefined}
                lineWidth={5}
                matrix={boxData.matrix}
                renderOrder={1000}
                scale={1}
                selectedColor={undefined}
                disableSliders
                enabled
                onDrag={(l, deltaL, w) =>
                    handleOnArticleTransformation(
                        l,
                        deltaL,
                        w,
                        snappingRotation,
                        positionUpdated,
                        selectedControlRef.current
                    )
                }
                onDragEnd={() => handleOnArticleTransformationEnd(true)}
                onDragStart={handleOnDragStart}
            >
                <mesh name="raycastable" renderOrder={900} onPointerDown={handleOnDragArticleStart}>
                    <boxGeometry args={[boxData.boxSize.x, boxData.boxSize.y, boxData.boxSize.z]} />
                    <meshBasicMaterial
                        color={0x649fda}
                        depthTest={false}
                        opacity={0.16}
                        side={DoubleSide}
                        transparent
                    />
                </mesh>

                {selectedControl && (
                    <Html distanceFactor={0.005} position={[0.7, 0.7, 0]} center>
                        <MantineOverrideProvider>
                            <div
                                style={{
                                    background: 'white',
                                    color: PRIMARY_COLOR_ACCENT_HEX,
                                    padding: '6px 8px',
                                    borderRadius: 6,
                                    whiteSpace: 'nowrap',
                                    pointerEvents: 'auto',
                                    border: '1px solid #c1c2c6',
                                    boxShadow: '0px 2px 4px 0px #0000001a'
                                }}
                            >
                                <TransformationLabel
                                    positionUpdated={positionUpdated}
                                    prevRadians={prevRadians}
                                    selectedControl={selectedControl}
                                    snappingRotation={snappingRotation}
                                />
                            </div>
                        </MantineOverrideProvider>
                    </Html>
                )}
            </PivotControls>
        </group>
    );
};
